import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Globe, ThumbsUp, MessageCircle, Share2, Heart, Send, Bookmark, Repeat2, Music2, MoreHorizontal } from "lucide-react";
import { NETS, AccountAvatar, mediaUrl, isStory } from "@/lib/social";
import { PlayableVideo } from "@/components/social/PlayableVideo";

function Media({ media, aspect = "aspect-square", rounded = "", fit = "object-cover", onIndex }) {
  const [i, setIdx] = useState(0);
  const setI = (n) => { setIdx(n); onIndex?.(n); };
  if (!media?.length) return null;
  const m = media[Math.min(i, media.length - 1)];
  return (
    <div className={`relative bg-black ${aspect} ${rounded} overflow-hidden`} data-testid="preview-media">
      {m.kind === "video" ? <PlayableVideo key={m.id} src={mediaUrl(m.id)} />
        : <img key={m.id} src={mediaUrl(m.id)} alt="" className={`h-full w-full ${fit}`} />}
      {media.length > 1 && (
        <>
          {i > 0 && <button data-testid="preview-prev" onClick={() => setI(i - 1)} className="absolute z-10 left-2 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-7 sm:w-7 rounded-full bg-white/90 flex items-center justify-center shadow"><ChevronLeft className="h-4 w-4" /></button>}
          {i < media.length - 1 && <button data-testid="preview-next" onClick={() => setI(i + 1)} className="absolute z-10 right-2 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-7 sm:w-7 rounded-full bg-white/90 flex items-center justify-center shadow"><ChevronRight className="h-4 w-4" /></button>}
          <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1 pointer-events-none">{media.map((x, k) => <span key={x.id} className={`h-1.5 w-1.5 rounded-full ${k === i ? "bg-white" : "bg-white/50"}`} />)}</div>
        </>
      )}
    </div>
  );
}

function Caption({ text, limit = 180, moreLabel = "See more", cls = "" }) {
  const [open, setOpen] = useState(false);
  const long = (text || "").length > limit;
  return (
    <p className={`whitespace-pre-wrap break-words text-[13px] leading-snug text-[#1c1e21] ${cls}`}>
      {open || !long ? text : `${text.slice(0, limit).trimEnd()}…`}
      {long && !open && <button onClick={() => setOpen(true)} className="block text-[#385898] text-[13px] mt-0.5">{moreLabel}</button>}
    </p>
  );
}

const when = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }) : "Not scheduled");

function StoryPreview({ post, account, ws, net, name }) {
  const ig = net === "instagram";
  const n = Math.max(1, post.media?.length || 1);
  const [cur, setCur] = useState(0);
  return (
    <div className="relative rounded-2xl bg-black overflow-hidden w-full max-w-[300px] mx-auto aspect-[9/16]" data-testid={`preview-story-${net}`}>
      {post.media?.length ? <Media media={post.media} aspect="h-full" fit="object-contain" onIndex={setCur} /> : <div className="h-full flex items-center justify-center text-xs text-white/60 px-6 text-center">Stories need an image or video</div>}
      <div className="absolute inset-x-0 top-0 px-2.5 pt-2.5 pb-6 bg-gradient-to-b from-black/50 to-transparent pointer-events-none">
        <div className="flex gap-1 mb-2.5">{Array.from({ length: n }).map((_, k) => <span key={k} className={`h-0.5 flex-1 rounded-full ${k <= cur ? "bg-white" : "bg-white/40"}`} />)}</div>
        <div className="flex items-center gap-2 text-white"><AccountAvatar a={account} ws={ws} size={28} /><span className="text-[12px] font-semibold truncate">{ig ? (account?.handle || name).replace(/^@/, "") : name}</span><span className="text-[11px] opacity-70">{when(post.scheduled_at).split(",")[0]}</span><MoreHorizontal className="h-4 w-4 ml-auto" /></div>
      </div>
      <div className="absolute inset-x-0 bottom-0 px-3 pb-3 pt-8 bg-gradient-to-t from-black/50 to-transparent flex items-center gap-2.5 pointer-events-none">
        <span className="flex-1 rounded-full border border-white/70 text-white/80 text-[12px] px-3.5 py-1.5">{ig ? "Send message" : "Reply…"}</span>
        {ig ? <><Heart className="h-5 w-5 text-white" /><Send className="h-5 w-5 text-white" /></> : <ThumbsUp className="h-5 w-5 text-white" />}
      </div>
    </div>
  );
}

export function PostPreview({ post, account, ws }) {
  const net = account?.network || "facebook";
  const name = account?.name || ws?.name || "Your brand";
  const N = NETS[net];
  const text = post.caption || "";
  if (isStory(post) && (net === "instagram" || net === "facebook")) return <StoryPreview post={post} account={account} ws={ws} net={net} name={name} />;
  if (net === "instagram") {
    return (
      <div className="rounded-xl border border-[#EEEFEF] bg-white overflow-hidden max-w-[400px] mx-auto" data-testid="preview-instagram">
        <div className="flex items-center gap-2.5 px-3 py-2.5"><AccountAvatar a={account} ws={ws} size={30} /><span className="text-[13px] font-semibold flex-1 truncate">{account?.handle || name}</span><MoreHorizontal className="h-4 w-4" /></div>
        {post.media?.length ? <Media media={post.media} aspect="aspect-[4/5]" fit="object-contain" /> : <div className="aspect-[4/5] bg-[#F6F6FD] flex items-center justify-center text-xs text-[#9FA1A4]">Instagram posts need an image or video</div>}
        <div className="px-3 pt-2.5 pb-3 space-y-1.5">
          <div className="flex items-center gap-3.5"><Heart className="h-5 w-5" /><MessageCircle className="h-5 w-5" /><Send className="h-5 w-5" /><Bookmark className="h-5 w-5 ml-auto" /></div>
          <Caption text={text} limit={125} moreLabel="more" cls="[&]:text-[#06060C]" />
          <p className="text-[10px] uppercase text-[#9FA1A4]">{when(post.scheduled_at)}</p>
        </div>
      </div>
    );
  }
  if (net === "tiktok") {
    return (
      <div className="relative rounded-2xl bg-black overflow-hidden w-full max-w-[300px] mx-auto aspect-[9/16]" data-testid="preview-tiktok">
        {post.media?.length ? <Media media={post.media} aspect="h-full" /> : <div className="h-full flex items-center justify-center text-xs text-white/60 px-6 text-center">TikTok posts need a video or images</div>}
        <div className="absolute right-2 bottom-24 flex flex-col items-center gap-4 text-white pointer-events-none"><AccountAvatar a={account} ws={ws} size={36} /><Heart className="h-6 w-6" /><MessageCircle className="h-6 w-6" /><Share2 className="h-6 w-6" /></div>
        <div className="absolute left-3 right-14 bottom-3 text-white space-y-1 pointer-events-none">
          <p className="text-[13px] font-semibold">@{(account?.handle || name).replace(/^@/, "")}</p>
          <p className="text-[12px] leading-snug line-clamp-3 whitespace-pre-wrap">{text}</p>
          <p className="text-[11px] flex items-center gap-1 opacity-80"><Music2 className="h-3 w-3" /> Original sound · {name}</p>
        </div>
      </div>
    );
  }
  const li = net === "linkedin";
  return (
    <div className="rounded-xl border border-[#EEEFEF] bg-white overflow-hidden max-w-[460px] mx-auto" data-testid={`preview-${net}`}>
      <div className="flex items-start gap-2.5 px-3.5 pt-3">
        <AccountAvatar a={account} ws={ws} size={40} />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-[#06060C] truncate">{name}</p>
          <p className="text-[11px] text-[#65676B] flex items-center gap-1">{li ? `${account?.kind || "Page"} · ` : ""}{when(post.scheduled_at)} · <Globe className="h-3 w-3" /></p>
        </div>
        <span className="h-5 w-5 rounded-full flex items-center justify-center text-white" style={{ background: N.color }}><N.icon className="h-3 w-3" /></span>
      </div>
      <Caption text={text} limit={li ? 210 : 180} moreLabel={li ? "…see more" : "See more"} cls="px-3.5 pt-2.5 pb-3" />
      {post.media?.length > 0 && <Media media={post.media} aspect={li ? "aspect-[1.91/1]" : "aspect-[4/5]"} fit={li ? "object-cover" : "object-contain"} />}
      <div className="flex items-center justify-around border-t border-[#EEEFEF] py-2 text-[12px] text-[#65676B]">
        <span className="flex items-center gap-1.5"><ThumbsUp className="h-4 w-4" /> Like</span><span className="flex items-center gap-1.5"><MessageCircle className="h-4 w-4" /> Comment</span>
        {li ? <span className="flex items-center gap-1.5"><Repeat2 className="h-4 w-4" /> Repost</span> : <span className="flex items-center gap-1.5"><Share2 className="h-4 w-4" /> Share</span>}
      </div>
    </div>
  );
}
